'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/hooks/use-auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Eye, EyeOff } from 'lucide-react';
import { t } from '@/lib/i18n';

/**
 * Login vía Instance (Seller Center) — /login/instance.
 * Convive con /login (tradicional) durante la transición. Solo login:
 * sin registro ni recuperación de contraseña (eso se gestiona en el
 * Seller Center).
 */
export default function LoginInstancePage() {
  const router = useRouter();
  const { loginInstance } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  function validate(): string | null {
    if (!email.includes('@')) return t('invalidEmail');
    // La política de passwords es del Seller Center; acá solo que venga algo.
    if (!password) return t('passwordRequired');
    return null;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    setLoading(true);

    try {
      await loginInstance(email, password);
      router.push('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('loginFailed'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card className="w-full max-w-sm shadow-lg">
      <CardHeader className="items-center pb-2 pt-8">
        <h1 className="text-2xl font-bold tracking-tight">{t('appName')}</h1>
        <p className="text-sm text-muted-foreground">{t('signInInstanceSubtitle')}</p>
      </CardHeader>
      <CardContent className="pt-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div role="alert" className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">{t('email')}</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              disabled={loading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">{t('password')}</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                disabled={loading}
                className="pr-10"
              />
              {/* Sugerencia de Instance (jul-2026): visualizar contraseña. */}
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Degradado de la marca Instance (el login tradicional mantiene el azul). */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full border-0 text-white transition-opacity hover:opacity-90"
            style={{ background: 'linear-gradient(90.14deg, #59c1dc -2.56%, rgba(18, 189, 194, 0.78) 86%)' }}
          >
            {loading ? t('signingIn') : t('signIn')}
          </Button>
        </form>

        {/* Volver al login tradicional mientras dura la transición. */}
        <div className="mt-4 border-t pt-4 text-center">
          <Link
            href="/login"
            className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            {t('backToTraditionalLogin')}
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
